/**
 * Wire types for the Brand API, mirroring the Python `to_dict()` shapes in `api/models.py` and
 * `api/fonts.py`.
 *
 * The shape is **flat**. The Python model groups the fields into a palette, a typography pair and
 * an author block so the aggregate stays readable, but that grouping is spread on the way out:
 * a consumer reads `primary_color`, not `colors.primary`. Keeping the wire flat is what lets a
 * renderer in any language treat the brand as a lookup table.
 *
 * Five fields are **derived when empty**: `logo_url`, `shop_url`, `privacy_url`, `terms_url` and
 * `refund_url` follow from the tenant's apex domain unless the brand overrides them. The server
 * fills them in on every read and every write, so they are non-optional here — a consumer never has
 * to know which of them were stored.
 */

// The two CSS helpers below delegate to `theme.ts`, which imports `COLOR_FIELDS` from here. The cycle
// is deliberate and inert: neither module reads the other's binding while it is being evaluated, only
// inside a function body.
import { brandFontFaceCss, brandVariables } from './theme';

/** A hex colour, normalized server-side to lowercase six-digit form (`#f43f5e`). */
export type Hex = string;

/** A font-family slug from the catalog (`oswald`, `playfair-display`, …). */
export type FontSlug = string;

/**
 * A language the brand can address a stranger in — the fleet's two, same slugs as `products`' and
 * `books`' own `Language`.
 *
 * A union rather than `string` because the API validates against exactly these and refuses anything
 * else: a consumer that switches on it should be told at compile time about the case it forgot, and
 * there is no third value to forget yet.
 */
export type BrandLanguage = 'en' | 'es';

/** Every language a brand may choose, for a picker. Same order the API's enum declares them in. */
export const BRAND_LANGUAGES: readonly BrandLanguage[] = ['en', 'es'];

/**
 * The typeface ROLE a family may fill — each one a brand field: `title` is `primary_font_family`,
 * `text` is `secondary_font_family`, `code` is `mono_font_family`. The API refuses a family in a role
 * it does not list.
 *
 * ⚠️ Three, and there was a fourth: `handwriting` was retired on 2026-10-02 so the brand has three
 * kinds of text. The twelve script-only families left the catalog with it; Leckerli One stayed, as a
 * title face.
 */
export type FontRole = 'title' | 'text' | 'code';

/** One family in the catalog, with its faces resolved to absolute URLs on `brand.<apex>`. */
export interface Font {
  family: string;
  slug: FontSlug;
  /** `sans` | `serif` | `display` | `mono` — what the picker groups by. */
  category: string;
  /** The weights Google publishes for the family. A family with only `[400]` reuses it for bold. */
  weights: number[];
  /** The CSS fallback list *after* the family itself. */
  stack: string;
  /** The roles this family is made for: Anton is `['title']`, Inter `['text']`, a mono `['code']`. */
  roles: FontRole[];
  /** The full CSS value, family first: `'Oswald', 'Arial Narrow', Impact, sans-serif`. */
  css_stack: string;
  /** Absolute `.ttf` URLs, keyed by weight. `700` is the regular face for a single-weight family. */
  faces: Record<string, string>;
}

/**
 * A brief — the brand's `audience`, `positioning` and `tone`, and an avatar's `personality`: a JSON
 * object with free keys (2026-10-30, the API's `instructions`), read by agents rather than people.
 * The API answers a record stored before then as `{text: …}`; `string` stays in the union only
 * for a client that cached the old shape.
 */
export type Brief = Record<string, unknown> | string;

/** The tenant's brand. One record; there is no list and no id to pass. */
export interface Brand {
  id: string;
  slug: string;

  // ── Identity ────────────────────────────────────────────────────────────────
  /** The brand name, as it appears on a cover, a landing page and a book. */
  title: string;
  /**
   * The brand's ONE line — what it promises, wherever the name appears (store, links, login, the
   * signature under every email). `slogan` sat beside it until 2026-10-04, when the owner merged
   * them. Not translated.
   */
  tagline: string;
  /** Who it speaks to — a BRIEF for an agent (a JSON object since 2026-10-30, see {@link Brief}).
   *  Private: never on `PublicBrandRecord`. */
  audience: Brief;
  /** The brand's public BLURB — one reader-facing paragraph (`free`'s subscribe page prints it).
   *  Until 2026-10-30 it also held the strategy; that is `positioning` now. */
  description: string;
  /** What the brand publishes, its pillars and the claims it may make — a private {@link Brief}. */
  positioning: Brief;
  /** The rules every VOICE of the brand follows — a private {@link Brief}. How one person speaks is
   *  that avatar's `personality`. */
  tone: Brief;
  /**
   * The words the brand wants to be FOUND by — its own list, in the order it ranks them.
   *
   * Read by two kinds of consumer: the public sites (`store`, `pages`) put them in their
   * `<meta name="keywords">`, and the `keywords` app starts from them — the seeds every source it
   * reads measures and discovers around. Trimmed and de-duplicated by the API; at most 25.
   */
  keywords: string[];

  // ── Narration ───────────────────────────────────────────────────────────────
  /**
   * The vox voice id every app's narration is synthesized in — the brand's SPOKEN voice.
   *
   * The counterpart to `tone`, which is the voice its copy is *written* in. A script can follow the
   * tone exactly and still be read aloud by somebody who sounds like a different brand.
   *
   * It is a plain string rather than a union, deliberately: vox owns the enum, it runs to thirty
   * entries and it grows, so a union here would be a copy that drifts — which the fleet has paid
   * for twice (see m7 `docs/COMPONENTS.md`). Ask `GET /v1/voices` for the current list.
   *
   * Never empty on the wire: an unset field resolves to vox's own lead voice server-side, because an
   * empty voice id is not "no narration", it is a synthesis that 400s.
   */
  narration_voice: string;

  // ── Language ────────────────────────────────────────────────────────────────
  /**
   * The language the brand addresses a STRANGER in — the default, and only the default.
   *
   * Two surfaces read it and neither can answer the question itself: the storefront greets a visitor
   * nobody has identified yet, and the login page is the screen somebody reads *before* there is an
   * account to hold a preference. Both fell back to English strings compiled into their own bundles
   * until this field existed, which is a tenant's language chosen by whoever wrote the component.
   *
   * ⚠️ It does **not** decide two things. A signed-in person's own language setting wins over it,
   * because overriding a stated preference with a tenant-wide default is worse than the fallback it
   * replaced. And a product page follows the PRODUCT's language, because a Spanish book is its own
   * product with its own page — rendering it in the shop's default would print a Spanish title
   * under English chrome.
   *
   * A union rather than a plain string, unlike `narration_voice` one field up, and the difference is
   * the one that docstring names: vox owns thirty voice ids and grows them, so a copy here would
   * drift. This is a closed set of two that the API validates and refuses outside of.
   *
   * Never empty on the wire — an unset brand answers `'en'`.
   */
  default_language: BrandLanguage;
  /**
   * The tenant's clock (2026-10-20), an IANA zone — what a time of day means across the fleet
   * (`posts` reads a queue's hours on it). Never empty on the wire — an unset brand answers `'UTC'`.
   */
  timezone: string;
  /**
   * Where the brand sells (2026-10-20): ISO alpha-2 countries and CLDR regions (`419` Latin
   * America, `EU`), as chosen. `[]` when the brand has not said.
   */
  regions?: string[];
  /** Derived, read-only: every country `regions` names (alpha-2), regions expanded. */
  countries?: string[];
  /** Derived, read-only: the same countries as alpha-3 (Search Console's codes). */
  alpha3?: string[];

  // ── Support ─────────────────────────────────────────────────────────────────
  /**
   * Where a buyer writes when something is wrong — printed by `store`'s footer, `checkout`'s form
   * and thank-you page, and `subscriptions`' pages under "contact support". Validated as an address
   * by the API.
   *
   * ⚠️ `''` when the operator has not set one, and there is **no fallback**: a page shows nothing
   * rather than inventing an address. (The tenant's own inbox is not the brand's: it is the dns
   * app's `infra.dns.admins` since 2026-10-10.)
   */
  support_email: string;
  /**
   * The brand's postal address (2026-10-30), up to six lines and 300 characters. `notify` prints it
   * under the unsubscribe link of every MARKETING email, as anti-spam law asks; no sender passes it.
   * `''` when unset — the footer then prints no address line. Private: `GET /v1/public` does not
   * answer it. Optional so a payload cached before the field existed still type-checks.
   */
  address?: string;

  // ── Mark ────────────────────────────────────────────────────────────────────
  /** The brand mark. Derived from `icons.<apex>` when unset — never empty on the wire. */
  logo_url: string;

  // ── Palette ─────────────────────────────────────────────────────────────────
  /** What the brand leads with. Was `accent` in the old `offers` `brand.yml`. */
  // ── The palette: six roles since 2026-10-04 ─────────────────────────────────
  //
  // ⚠️ Four accent VARIANTS (`primary_2_color` and friends) were on this record for one day. The
  // owner removed them — "brand app should define primary and secondary but not primary1,
  // primary2, etc." — because nobody ever chose those hexes: they were derived from the lead
  // colour on every read. A renderer that needs a second distinguishable accent derives one.
  primary_color: Hex;
  secondary_color: Hex;
  dark_color: Hex;
  white_color: Hex;
  grey_color: Hex;
  error_color: Hex;
  /**
   * ⚠️ **Retired 2026-10-03, still answered.** Six roles described a dark web console — a
   * background, two surfaces, a border — and meant nothing to a book cover, a thumbnail or a
   * certificate, which is most of what this fleet renders. They are DERIVED from the ten above by
   * the API (`models.LEGACY_COLORS`) and are read-only: a PATCH naming one is ignored.
   *
   * They are typed so that an unmigrated consumer still compiles. New code reads the roles above:
   * `background` → `dark`, `text` → `white`, `muted` → `grey`, `danger` → `error`, `success` →
   * `primary`, and a surface is `dark` blended a few percent towards `white` if it needs one.
   *
   * @deprecated Use the six roles above.
   */
  background_color: Hex;
  /** @deprecated Derived from `dark_color`. */
  background_alt_color: Hex;
  /** @deprecated Derived from `dark_color`. */
  surface_color: Hex;
  /** @deprecated Derived from `dark_color`. */
  surface_alt_color: Hex;
  /** @deprecated Use `white_color`. */
  text_color: Hex;
  /** @deprecated Use `grey_color`. */
  muted_color: Hex;
  /** @deprecated Derived from `dark_color` and `grey_color`. */
  border_color: Hex;
  /** @deprecated Use `primary_color` — a simplified palette has no separate "good" colour. */
  success_color: Hex;
  /** @deprecated Use `error_color`. */
  danger_color: Hex;

  // ── Typography ──────────────────────────────────────────────────────────────
  primary_font_family: FontSlug;
  secondary_font_family: FontSlug;
  /**
   * The monospace face. It has a slot of its own because `code` renders its entire output in one,
   * and without it the app whose whole product is type would be the only one that could not follow
   * the brand.
   */
  mono_font_family: FontSlug;
  /**
   * The chosen families, resolved. Server-computed from the slugs above; never sent back.
   *
   * A role is `null` only when its slug names nothing in the catalog, which a save cannot produce
   * — the API validates against the same list. Treat it as "something is wrong here", not as a
   * choice the brand declined to make.
   */
  fonts?: {
    primary: Font | null;
    secondary: Font | null;
    mono: Font | null;
  };

  // ── The author block is NOT here ────────────────────────────────────────────
  //
  // `author_name`, `author_email`, `author_website`, `author_avatar_url` and the six prose fields
  // were on this record until 2026-09-12. They are an `Avatar`'s now — see the type below — because
  // one author per tenant is the same ceiling one `narration_voice` was: a fleet that can name one
  // writer cannot publish two, and an interview needs both of them to have a biography.
  //
  // A consumer that wants the byline reads an avatar (`useAvatars`, or `avatar()` in the Python
  // connector, which answers the tenant's default when given no id).

  // ── Links ───────────────────────────────────────────────────────────────────
  //
  // There are no template slugs here, and their absence is load-bearing. Which Resources mockup a
  // book jacket or a bundle cover is photographed on is a decision about how ONE app renders —
  // `books` and `offers` answer it differently for the same tenant — so each holds its own
  // `src/<brand>/template.yml`. The brand is read BY its consumers; it does not configure them.
  /** Derived from the apex when unset — never empty on the wire. */
  shop_url: string;
  privacy_url: string;
  terms_url: string;
  /** The refund policy, `https://legal.<apex>/refunds`. A page of its own on the legal site, not a
   *  clause of the terms — a payment processor asks for one at its own URL. */
  refund_url: string;

  // ── The link page ───────────────────────────────────────────────────────────
  /**
   * The buttons `links.<apex>` draws, in page order (2026-10-04). Edited in the console's Links tile
   * and answered by `GET /v1/public`, because a link page is read by strangers. Written whole: a save
   * that names `links` replaces the list, one that does not leaves it alone. `[]` when unset.
   */
  links: BrandLink[];
  /**
   * The link page's background (2026-10-04): a `resources` stock-image slug, and the url the API
   * resolved it to on save. Pages draw `background_url`; the slug is console-only (not public).
   */
  background_image?: string;
  background_url: string;
  /**
   * The sign-in page's hero video (2026-10-05): a `resources` stock-video slug, and the url the API
   * resolved it to on save. `login` plays `login_video_url`, and its own footage when it is empty.
   */
  login_video?: string;
  login_video_url: string;
  /** Media's 32px / 256px thumbnails of the mark — every public page's favicon (derived). */
  favicon_url: string;
  touch_icon_url: string;

  version?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * What a link points at — the icon beside its title. The API's `LinkType`, same slugs, same order;
 * it refuses anything else. `link` is the plain chain mark for whatever the list does not name.
 */
export const LINK_TYPES = [
  'website',
  'store',
  'amazon',
  'github',
  'linkedin',
  'instagram',
  'youtube',
  'x',
  'facebook',
  'tiktok',
  'whatsapp',
  'telegram',
  'email',
  'link',
] as const;

export type LinkType = (typeof LINK_TYPES)[number];

/** One button on the link page: what it is (the icon), what it says, where it goes. */
export interface BrandLink {
  type: LinkType;
  /** The words on the button. Required — the API refuses a blank one. */
  title: string;
  /** `https://`, `http://` or `mailto:` — the API refuses any other scheme. */
  url: string;
}

/** The palette field names, in the order the console shows them. */
/**
 * The palette fields a console EDITS and a page themes from, in the order the console shows them.
 *
 * ⚠️ The retired six (`background_color` and friends) are deliberately NOT here. They are still on
 * the wire and still typed on `Brand`, but they are derived server-side, so putting them in this
 * list would give the console a colour picker that writes a field the API ignores — and
 * `brandVariables` would emit a variable per legacy role, which is the vocabulary this change
 * exists to retire. A consumer that still needs `--brand-surface` passes it through `colors`.
 */
export const COLOR_FIELDS = [
  'primary_color',
  'secondary_color',
  'dark_color',
  'white_color',
  'grey_color',
  'error_color',
] as const;

/** The retired roles, still answered by the API and derived from the ten above. @deprecated */
export const LEGACY_COLOR_FIELDS = [
  'background_color',
  'background_alt_color',
  'surface_color',
  'surface_alt_color',
  'text_color',
  'muted_color',
  'border_color',
  'success_color',
  'danger_color',
] as const;

export type LegacyColorField = (typeof LEGACY_COLOR_FIELDS)[number];

export type ColorField = (typeof COLOR_FIELDS)[number];

/** The typography field names, in the order the console shows them. */
export const FONT_FIELDS = [
  'primary_font_family',
  'secondary_font_family',
  'mono_font_family',
] as const;

export type FontField = (typeof FONT_FIELDS)[number];

/**
 * A CSS custom-property block for the brand, ready to attach to an element's `style`.
 *
 * Every consumer that themes itself from the brand wants the same thing — the palette as CSS
 * variables — and each one writing its own mapping is how `offers` ended up with a hex in a
 * template that nothing could re-theme. The names mirror the field names (`--brand-primary`), so
 * the CSS reads like the record.
 *
 * A thin call into `theme.ts`, which owns the mapping for every consumer: the fleet's consoles want
 * it under `--brand-*` and the public pages want their own namespace, and that is the only
 * difference between them. It therefore also gets what that module adds — every colour validated as
 * a hex, and `--brand-on-primary` derived from the fill so a button's label is legible on a bright
 * accent (see the shipped failure documented there).
 */
export function cssVariables(brand: Brand): Record<string, string> {
  return brandVariables(brand, { prefix: 'brand' });
}

/**
 * The `@font-face` rules for every family the brand publishes, as a stylesheet string.
 *
 * A consumer that renders brand text in the browser needs the faces declared before the family
 * name means anything, and the URLs are only knowable at runtime (they carry the tenant's apex).
 *
 * ⚠️ It covers **all four roles**, and the hand was missing until 2026-09-12. That omission is the
 * whole reason `carousels`, `reflections` and `wisdom` load Caveat from Google Fonts themselves:
 * there was no way to declare the brand's hand in a browser, so each composer hardcoded the family
 * its renderer happened to default to. A helper that emits three of four roles does not centralize
 * typography, it just moves where the fourth gets decided.
 */
export function fontFaceCss(brand: Brand): string {
  return brandFontFaceCss(brand.fonts);
}
